import { motion } from "framer-motion";
import { Brush, Layers, PackageCheck } from "lucide-react";

const steps = [
    {
        num: "01",
        icon: Brush,
        title: "Prep & Mask",
        text: "The base sneaker is stripped, cleaned and masked by hand. Every line of the artwork is planned before a single drop of paint leaves the can.",
    },
    {
        num: "02",
        icon: Layers,
        title: "Spray & Layer",
        text: "Layer after layer of spray paint and brushwork builds the design — fades, scratches, motifs. Hours of work for a finish no machine can fake.",
    },
    {
        num: "03",
        icon: PackageCheck,
        title: "Seal & Ship",
        text: "A matte protective sealant locks the art in against rain and concrete. Your one-of-one pair is boxed and shipped straight to your door.",
    },
];

export const ProcessSection = () => (
    <section id="process" data-testid="process-section" className="border-t border-vault-border bg-vault-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-28 md:py-40">
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="mb-16"
            >
                <p className="font-mono text-xs tracking-[0.3em] text-vault-red">CHAPTER 02</p>
                <h2 className="mt-3 font-display text-5xl uppercase leading-none text-stone-900 md:text-7xl">The Craft</h2>
            </motion.div>

            <div className="grid gap-6 lg:grid-cols-[1fr_1fr_1fr_1.2fr]">
                {steps.map((s, i) => (
                    <motion.div
                        key={s.num}
                        data-testid={`process-step-${s.num}`}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                        className="group border border-vault-border bg-vault-black p-8 transition-colors hover:border-vault-red"
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-sm text-vault-red">{s.num}</span>
                            <s.icon size={20} className="text-stone-400 transition-colors group-hover:text-vault-red" />
                        </div>
                        <h3 className="mt-10 font-display text-2xl uppercase text-stone-900">{s.title}</h3>
                        <p className="mt-4 text-sm leading-relaxed text-stone-600">{s.text}</p>
                    </motion.div>
                ))}
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="clip-frame relative min-h-64 overflow-hidden border border-vault-border"
                >
                    <img
                        src="https://images.pexels.com/photos/7120502/pexels-photo-7120502.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="Spray paint cans in the Vault Deportivos studio"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-vault-black/80 to-transparent" />
                    <p className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] text-stone-600">
                        THE TOOLS OF THE TRADE
                    </p>
                </motion.div>
            </div>
        </div>
    </section>
);
